/**
 * AccountMenu — the shell header's identity + session control (SCRUM-156;
 * rebuilt 2026-08).
 *
 * WHAT IT WAS. A bare avatar disc that opened a two-line popover: the user's
 * email in grey 12px, and "Log out". Three problems, all visible on the
 * shipped header:
 *   1. It was the ONLY account affordance, and it said nothing about who you
 *      were signed in as until you clicked it.
 *   2. It rendered identically for a signed-OUT visitor — the shell hosts
 *      public routes (marketplace, shops, library), so an anonymous user got
 *      an avatar whose only action was "Log out" of a session they never had,
 *      and a `/me` request that could only 401.
 *   3. It was a dead end: every account destination the app has (profile,
 *      settings, the seller portal) was reachable only by hunting through the
 *      "More" tab.
 *
 * WHAT IT IS NOW. Two states, both honest:
 *   • Signed out → a "Sign in" control. No avatar, no phantom session, and
 *     `useMe` is not even enabled, so a public page makes no 401 round-trip.
 *     With a guest conversation in this browser (SCRUM-276) the door returns
 *     to it and records the claim intent.
 *   • Signed in → the avatar plus the display name (from `lg` up, where there
 *     is room), opening a ruled slip of a menu: an identity block naming who
 *     you are, then the account destinations, then Log out on its own course.
 *     The seller-portal row appears only when `profile.is_seller` — a real
 *     fact from `/me`, never a guess.
 *
 * Log out reuses the shared `logout()` (POST /auth/logout + clearAuth + clear
 * the TanStack cache — SCRUM-138), which deliberately swallows a failing POST
 * because clearing the in-memory token is what actually signs this tab out.
 * That means there is no failure path to report here; what WAS missing is the
 * confirmation, so the user landing back on the marketing page knows the
 * session ended rather than guessing. One `notify.success` with a stable id.
 *
 * Hand-rolled dropdown (no `@radix-ui` — not a project dependency; the same
 * constraint `HeaderSearchBar` and the filter Sheet document): click to open,
 * outside click / Escape / Tab-out to close, Escape returns focus to the
 * trigger, arrow keys walk the rows. Every row is a ≥44px target.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  LogIn,
  LogOut,
  Settings,
  Store,
  User,
} from "lucide-react";

import { pressClass } from "@/components/motion";
import { withReturnTo } from "@/features/auth/destination";
import { guestReturnTo, recordClaimIntent, useGuestPointer } from "@/features/chat/guest/pointer";
import { logout, useMe } from "@/features/settings/api";
import {
  selectAuthBootstrapped,
  selectIsAuthenticated,
  useAuthStore,
} from "@/lib/auth-store";
import { iconProps } from "@/lib/icons";
import { menuVariants } from "@/lib/motion";
import { notify } from "@/lib/notify";
import { useGardenMotion } from "@/lib/use-garden-motion";

/** Up-to-two-character initials for the avatar (decorative — the name is
 *  spelled out beside it and inside the menu). */
function initialsOf(source: string): string {
  const trimmed = source.trim();
  if (trimmed === "") return "";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const letters =
    parts.length >= 2
      ? `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`
      : trimmed.slice(0, 2);
  return letters.toUpperCase();
}

interface MenuDestination {
  key: string;
  to: string;
  labelKey: string;
  Icon: typeof User;
}

export function AccountMenu(): ReactElement {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { reduced, overlay } = useGardenMotion();
  const authenticated = useAuthStore(selectIsAuthenticated);
  const bootstrapped = useAuthStore(selectAuthBootstrapped);
  const { data: profile } = useMe({ enabled: authenticated });
  const pointer = useGuestPointer();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  const close = useCallback((restoreFocus: boolean): void => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  // Close on outside pointer / Escape / focus leaving — the same dismissal
  // contract as the language menu and the search overlay.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") close(true);
    };
    const onFocusIn = (event: FocusEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open, close]);

  const handleLogout = useCallback(async (): Promise<void> => {
    setLoggingOut(true);
    try {
      await logout();
      close(false);
      navigate("/", { replace: true });
      notify.success(t("account.loggedOut"), { id: "session-logout" });
    } finally {
      setLoggingOut(false);
    }
  }, [close, navigate, t]);

  const destinations = useMemo<MenuDestination[]>(() => {
    const rows: MenuDestination[] = [
      { key: "profile", to: "/account/profile", labelKey: "account.profile", Icon: User },
      { key: "settings", to: "/settings", labelKey: "account.settings", Icon: Settings },
    ];
    if (profile?.is_seller) {
      rows.push({
        key: "seller",
        to: "/sellers/me",
        labelKey: "account.sellerPortal",
        Icon: Store,
      });
    }
    return rows;
  }, [profile?.is_seller]);

  const focusRow = useCallback(
    (index: number): void => {
      const rows = itemRefs.current.filter(Boolean);
      if (rows.length === 0) return;
      const next = ((index % rows.length) + rows.length) % rows.length;
      rows[next]?.focus();
    },
    [],
  );

  const onRowKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>, index: number): void => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        focusRow(index + 1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        focusRow(index - 1);
      } else if (event.key === "Tab") {
        setOpen(false);
      }
    },
    [focusRow],
  );

  // ── Booting ───────────────────────────────────────────────────────────────
  // The access token lives in JS memory only, so a hard reload starts signed
  // OUT and flips to signed IN when the silent refresh lands. Rendering the
  // signed-out door during that window makes the header flash "Sign in" at a
  // user who is signed in. Hold a quiet placeholder of the same size instead.
  if (!bootstrapped && !authenticated) {
    return (
      <span
        aria-hidden="true"
        data-testid="account-menu-loading"
        className="inline-block size-9 shrink-0 rounded-full bg-[color:var(--g-sunken)]"
      />
    );
  }

  // ── Signed out ────────────────────────────────────────────────────────────
  // The shell wraps public routes too. A visitor gets a door, not a disc.
  if (!authenticated) {
    // Guest chat (SCRUM-276): while this browser holds a guest conversation,
    // «Войти» returns to it — always `/chat?t=<id>` — and records the claim
    // intent, so whichever account signs in claims it without asking (W2.6).
    // Recorded on a middle-click too (`onAuxClick`) — a plain `onClick` never
    // fires for the browser's own "open in a new tab" gesture, and that must
    // not race the intent write against the new tab's own sign-in.
    const guestConversation = pointer !== null && pointer.hasSession;
    const returnTo = guestConversation
      ? guestReturnTo(pointer)
      : location.pathname + location.search;
    return (
      <Link
        to={withReturnTo("/login", returnTo)}
        onClick={
          guestConversation
            ? () => {
                recordClaimIntent();
              }
            : undefined
        }
        onAuxClick={
          guestConversation
            ? () => {
                recordClaimIntent();
              }
            : undefined
        }
        data-testid="account-menu-signin"
        className={[
          "inline-flex h-11 shrink-0 items-center gap-2 rounded-[10px] px-3",
          "bg-primary text-sm font-semibold text-[color:var(--g-on-primary)]",
          "transition-opacity hover:opacity-90",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          pressClass("cta"),
        ].join(" ")}
      >
        <LogIn {...iconProps("sm")} />
        <span className="whitespace-nowrap">{t("nav.login")}</span>
      </Link>
    );
  }

  const name = profile?.display_name?.trim() ?? "";
  const initials = initialsOf(name !== "" ? name : (profile?.email ?? ""));
  const rowClass = [
    "flex min-h-[44px] w-full items-center gap-2.5 px-3 text-left text-sm font-medium",
    "text-foreground transition-colors hover:bg-[color:var(--g-sunken)]",
    "focus-visible:bg-[color:var(--g-sunken)] focus-visible:outline-none",
  ].join(" ");

  return (
    <div className="relative shrink-0" ref={rootRef} data-testid="account-menu">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          name !== ""
            ? t("account.openMenuFor", { name })
            : t("account.openMenu")
        }
        data-testid="account-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={[
          "inline-flex h-11 max-w-[13rem] items-center gap-2 rounded-[10px] pl-1 pr-1 lg:pr-2.5",
          "border border-transparent text-foreground transition-colors",
          "hover:bg-[color:var(--g-sunken)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          open ? "bg-[color:var(--g-sunken)]" : "",
          pressClass("cta"),
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span
          aria-hidden="true"
          className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-[0.8125rem] font-bold text-[color:var(--g-on-primary)]"
        >
          {initials !== "" ? initials : <User {...iconProps("sm")} />}
        </span>
        <span className="hidden min-w-0 truncate text-sm font-semibold lg:block">
          {name !== "" ? name : (profile?.email ?? "")}
        </span>
        <ChevronDown
          {...iconProps("xs")}
          className={[
            "hidden shrink-0 text-[color:var(--g-ink-3)] transition-transform duration-150 lg:block",
            "motion-reduce:transition-none",
            open ? "rotate-180" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        />
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            role="menu"
            aria-label={t("account.menuLabel")}
            data-testid="account-menu-panel"
            className="absolute right-0 top-[calc(100%+6px)] z-50 w-[16.5rem] overflow-hidden rounded-[12px] border border-[color:var(--g-rule)] bg-popover text-popover-foreground shadow-[var(--g-shadow-lg)]"
            variants={reduced ? undefined : menuVariants}
            {...overlay()}
          >
            {/* Identity — who this session belongs to, stated before any
                action. The email is the account's true key, so it gets the
                full line rather than a truncated afterthought. */}
            <div className="flex items-start gap-3 border-b border-[color:var(--g-rule)] px-3 py-3">
              <span
                aria-hidden="true"
                className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-[color:var(--g-on-primary)]"
              >
                {initials !== "" ? initials : <User {...iconProps("sm")} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[color:var(--g-ink-3)]">
                  {t("account.signedInAs")}
                </span>
                {name !== "" ? (
                  <span className="mt-0.5 block truncate text-sm font-semibold text-foreground">
                    {name}
                  </span>
                ) : null}
                <span
                  className="block truncate text-xs text-[color:var(--g-ink-2)]"
                  data-testid="account-menu-email"
                >
                  {profile?.email ?? ""}
                </span>
              </span>
            </div>

            <ul className="g-ruled m-0 list-none p-0">
              {destinations.map((row, index) => (
                <li key={row.key}>
                  <Link
                    ref={(node) => {
                      itemRefs.current[index] = node;
                    }}
                    to={row.to}
                    role="menuitem"
                    data-testid={`account-menu-${row.key}`}
                    onClick={() => close(false)}
                    onKeyDown={(event) => onRowKeyDown(event, index)}
                    className={rowClass}
                  >
                    <row.Icon
                      {...iconProps("sm")}
                      className="shrink-0 text-[color:var(--g-ink-3)]"
                    />
                    <span className="min-w-0 flex-1 truncate">{t(row.labelKey)}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="border-t border-[color:var(--g-rule-strong)]">
              <button
                ref={(node) => {
                  itemRefs.current[destinations.length] = node;
                }}
                type="button"
                role="menuitem"
                disabled={loggingOut}
                onClick={() => void handleLogout()}
                onKeyDown={(event) => onRowKeyDown(event, destinations.length)}
                data-testid="account-menu-logout"
                className={`${rowClass} disabled:opacity-60`}
              >
                <LogOut
                  {...iconProps("sm")}
                  className="shrink-0 text-[color:var(--g-ink-3)]"
                />
                <span className="min-w-0 flex-1 truncate">{t("account.logout")}</span>
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default AccountMenu;
